import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatCard from "@/components/StatCard";
import { EngagementWidget } from "@/components/EngagementWidget";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { STATUS_META, STATUS_ORDER, PRIORITY_META, fmtDate, timeAgo } from "@/lib/brand";
import {
  Ticket, CheckCircle2, Clock, Users, Flag, AlertTriangle,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [engagement, setEngagement] = useState(null);
  const isClient = user.role === "client";

  const loadDashboard = useCallback(async () => {
    const [s, t] = await Promise.all([api.get("/dashboard/stats"), api.get("/tickets")]);
    setStats(s.data);
    setTickets(t.data);
    if (user.role !== "client") {
      api.get("/dashboard/engagement").then((r) => setEngagement(r.data)).catch(() => {});
    }
  }, [user.role]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  if (!stats) {
    return <div className="grid grid-cols-1 md:grid-cols-4 gap-6">{[0,1,2,3].map(i=>(
      <div key={i} className="h-28 rounded-md bg-white border border-slate-200 animate-pulse" />))}</div>;
  }

  const statusData = STATUS_ORDER.map((s) => ({ name: STATUS_META[s].label, value: stats.by_status[s] || 0, fill: STATUS_META[s].dot }));
  const priorityData = Object.entries(stats.by_priority).map(([k, v]) => ({ name: PRIORITY_META[k].label, value: v, fill: PRIORITY_META[k].color }));
  const recent = tickets.slice(0, 6);

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
          {isClient ? `Welcome, ${user.client?.name || user.name}` : "Agency Overview"}
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          {isClient ? "Your project status, tickets and performance at a glance." : "All client projects and active work across the board."}
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {!isClient && <StatCard testid="stat-clients" label="Active Clients" value={stats.active_clients ?? 0} icon={Users} accent="#F7941E" />}
        <StatCard testid="stat-open" label="Open Tickets" value={stats.open_tickets} icon={Ticket} accent="#3B82F6" />
        <StatCard testid="stat-progress" label="In Progress" value={stats.by_status.in_progress} icon={Clock} accent="#F59E0B" />
        <StatCard testid="stat-resolved" label="Resolved" value={stats.resolved_tickets} icon={CheckCircle2} accent="#10B981" />
        {isClient && <StatCard testid="stat-urgent" label="Urgent" value={stats.by_priority.urgent} icon={AlertTriangle} accent="#EF4444" />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-2 border-slate-200">
          <p className="font-display font-semibold text-slate-900 mb-4">Tickets by Status</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={statusData} barCategoryGap={28}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {statusData.map((d) => <Cell key={d.name} fill={d.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 border-slate-200">
          <p className="font-display font-semibold text-slate-900 mb-4">By Priority</p>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={priorityData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                {priorityData.map((d) => <Cell key={d.name} fill={d.fill} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 justify-center">
            {priorityData.map((d) => (
              <div key={d.name} className="flex items-center gap-1.5 text-xs text-slate-500">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.fill }} />{d.name}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {!isClient && engagement && <EngagementWidget engagement={engagement} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-2 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <p className="font-display font-semibold text-slate-900">Recent Activity</p>
            <button onClick={() => navigate("/tickets")} className="text-xs text-sky-600 hover:underline" data-testid="view-all-tickets">View all</button>
          </div>
          <div className="space-y-1">
            {recent.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No tickets yet.</p>}
            {recent.map((t) => (
              <button key={t.id} onClick={() => navigate(`/tickets/${t.id}`)} data-testid="recent-ticket"
                className="w-full flex items-center gap-3 px-2 py-2.5 rounded-md hover:bg-slate-50 text-left transition-colors">
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: STATUS_META[t.status].dot }} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-800 truncate">{t.title}</p>
                  <p className="text-[11px] text-slate-400">{t.client_name} · {timeAgo(t.updated_at)}</p>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded ${PRIORITY_META[t.priority].bg}`}>{PRIORITY_META[t.priority].label}</span>
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-5 border-slate-200">
          <p className="font-display font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Flag size={16} style={{ color: "var(--tenant-primary)" }} /> Milestones
          </p>
          {isClient && stats.milestones ? (
            <div className="space-y-4">
              {stats.milestones.length === 0 && <p className="text-sm text-slate-400">No milestones set.</p>}
              {stats.milestones.map((m) => (
                <div key={m.id} data-testid="dash-milestone">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-700 truncate pr-2">{m.title}</span>
                    <span className="text-slate-400 shrink-0">{m.progress}%</span>
                  </div>
                  <Progress value={m.progress} className="h-1.5" />
                  <p className="text-[11px] text-slate-400 mt-1">Due {fmtDate(m.due_date)}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-500 space-y-3">
              <p>Track delivery progress per client on the Milestones page.</p>
              <button onClick={() => navigate("/milestones")} className="text-sky-600 hover:underline text-sm" data-testid="go-milestones">
                Open Milestones →
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
